import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_BASE_URL } from '@core/injectors';
import { Observable } from 'rxjs';
import {
  Draft,
  DraftCreateRequest,
  DraftEntityType,
  DraftListResponse,
  DraftPayload,
  DraftUpdateRequest,
} from '../../models/draft/draft.model';

/**
 * The single HTTP layer for `/api/drafts/` (PLAT-4318).
 *
 * **One service, not four methods duplicated on each of the three data services.** The
 * superseded design gave every entity its own `/api/{entity}/draft/` sub-actions, so
 * `DataSourcesDataService`, `ConnectorsDataService` and `PipelinesDataService` each carried a
 * byte-identical `saveDraft`/`loadDraft`/`finalizeDraft`/`discardDraft` quartet differing only
 * in the interpolated URL segment. The entity pivot collapses that into one resource taking
 * `entity_type` as a field, so the duplication has no reason to survive — those four methods
 * are **removed** from all three services, not extended.
 *
 * `providedIn: 'root'`, unlike `WorkflowStateService`: this is a stateless HTTP wrapper with
 * nothing per-workflow-session about it, matching every other service in `core/services`.
 */
@Injectable({ providedIn: 'root' })
export class DraftService {
  constructor(
    private readonly http: HttpClient,
    @Inject(API_BASE_URL) private readonly apiBaseUrl: string,
  ) {}

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/api/drafts/`;
  }

  /**
   * `GET /api/drafts/?entity_type=` — every draft in the organisation, paginated (PLAT-4482). Each
   * carries `can_modify`: only its author or the CDO may change it.
   *
   * **New under the pivot.** The superseded backend unioned draft rows into each entity's own
   * `list()` response, so no draft-list endpoint existed and none was needed. A `Draft` row can
   * never appear in `GET /api/connectors/` any more — different table — so each list page must
   * call this itself and merge client-side.
   */
  list(entityType?: DraftEntityType): Observable<DraftListResponse> {
    const params = entityType ? new HttpParams().set('entity_type', entityType) : undefined;
    return this.http.get<DraftListResponse>(this.baseUrl, { params });
  }

  /**
   * `POST /api/drafts/` with `{entity_type, payload}`.
   *
   * There is no minimum payload at the API layer — `Draft.payload` is schema-agnostic, so
   * nothing here rejects an incomplete form. The per-flow gate on *when* Save draft becomes
   * available (step 2 for data sources and connectors, step 1 for pipelines) is a UX rule owned
   * by the workflow definition, not a constraint this call enforces.
   */
  create(entityType: DraftEntityType, payload: DraftPayload): Observable<Draft> {
    const body: DraftCreateRequest = { entity_type: entityType, payload };
    return this.http.post<Draft>(this.baseUrl, body);
  }

  /**
   * `GET /api/drafts/{id}/` — **this is the resume read.**
   *
   * A plain generic `retrieve()`, not a bespoke `/draft/` sub-action. Organisation-scoped by
   * queryset (PLAT-4482), so any draft the list shows can be read; changing one is refused (403)
   * unless the caller is its author or the CDO.
   */
  get(id: number): Observable<Draft> {
    return this.http.get<Draft>(`${this.baseUrl}${id}/`);
  }

  /** `PATCH /api/drafts/{id}/` with `{payload}` — the Save-draft call once an id exists. */
  update(id: number, payload: DraftPayload): Observable<Draft> {
    const body: DraftUpdateRequest = { payload };
    return this.http.patch<Draft>(`${this.baseUrl}${id}/`, body);
  }

  /**
   * `DELETE /api/drafts/{id}/` — a genuine hard delete.
   *
   * No soft-delete tombstone: nothing references a `Draft` row the way real entities are
   * referenced, and drafts never expire, so a tombstone would sit forever with nothing to clean
   * it up.
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}${id}/`);
  }

  /**
   * `POST /api/drafts/{id}/finalize/` — convert the draft into its real entity.
   *
   * The stored `payload` **is** the request; there is no body. On success the server creates the
   * entity through its own create serializer, deletes the `Draft` row and returns the entity. On
   * a 400 the `Draft` row survives untouched with its payload intact, so a failed finalize never
   * consumes the user's work.
   *
   * Typed loosely because the response shape is whichever entity the draft's `entity_type`
   * names; each caller narrows to the one thing it needs (the new id).
   */
  finalize<TEntity = { id: number }>(id: number): Observable<TEntity> {
    return this.http.post<TEntity>(`${this.baseUrl}${id}/finalize/`, {});
  }
}
